// YOSHI-10464: the glossary of the public Yoshi API - every response field worth explaining,
// with the key of its translated description.
//
// The parameter tables document what an integrator SENDS. Nothing documented what comes BACK: a
// reader with a response in front of them knew the names of the fields and not their meaning, and
// the one that costs money to get wrong - which number is the taxable base - was indistinguishable
// from the one next to it.
//
// This catalog used to feed notes painted over the JSON examples. It does not any more: a note per
// field reads fine over a five line body and turns an eighty line sale into a wall of prose, so the
// examples went back to being plain code and the descriptions moved here, into a page that is
// consulted rather than read. Nobody reads a glossary end to end, which is why it can afford to be
// exhaustive where an inline note could not.
//
// The fields are grouped by subject and never alphabetically: whoever opens this has a sale in
// front of them and wants the money fields together, not a walk from `amount` to `url`. Each group
// is an anchor of the page, so the index of the page lands on it.
//
// The registry still leaves out what explains itself - `uuid`, `name`, `created_at` - because a row
// that says nothing costs the reader the same attention as one that does.
//
// The prose is not here: every entry points at a key of the translation catalogs, like the rest of
// the portal, so a description reads in the language the reader picked. The field NAME and its TYPE
// are never translated: they are what the API answers.

import { DocsGlossaryGroup, DocsTableRow } from "../interfaces";

/** Where the translated descriptions live inside the catalogs, so the prefix is written once. */
const FIELD_PREFIX: string = "docs.fields";

/** Builds a row out of the field name, its type and the last segment of its translation key. */
const field = (name: string, type: string, key: string): DocsTableRow => {
  return {
    id: name,
    name,
    type,
    descriptionKey: `${FIELD_PREFIX}.${key}`,
    isRequired: null,
  };
};

/**
 * The glossary, in reading order.
 *
 * `starting_after` and `ending_before` travel in the request and no response echoes them back, but
 * they are part of the same pagination story as `has_more`, and a reader walking pages needs the
 * four of them in one place.
 */
export const DOCS_GLOSSARY_GROUPS: DocsGlossaryGroup[] = [
  {
    key: "envelope",
    fields: [
      field("object", "string", "object"),
      field("url", "string", "url"),
      field("has_more", "boolean", "hasMore"),
      field("total_rows", "integer", "totalRows"),
      field("starting_after", "string (uuid)", "startingAfter"),
      field("ending_before", "string (uuid)", "endingBefore"),
    ],
  },
  {
    key: "identifiers",
    fields: [
      field("code", "string", "code"),
      field("uuid_app_order", "string (uuid) | null", "uuidAppOrder"),
      field("sku", "string", "sku"),
    ],
  },
  {
    key: "classification",
    fields: [
      field("status", "integer", "status"),
      field("sale_type", "string", "saleType"),
      field("sold_at", "string (date-time)", "soldAt"),
      field("app_order_type", "integer", "appOrderType"),
      field("order_source", "string", "orderSource"),
      field("counted_sale_statuses", "integer[]", "countedSaleStatuses"),
      field("as_of", "string (date-time)", "asOf"),
    ],
  },
  {
    key: "money",
    fields: [
      field("currency", "string", "currency"),
      field("tax_rate", "number", "taxRate"),
      field("items_total", "integer", "itemsTotal"),
      field("sales_count", "integer", "salesCount"),
      field("total_ex_tax", "string (decimal)", "totalExTax"),
      field("total_tax", "string (decimal)", "totalTax"),
      field("total_inc_tax", "string (decimal)", "totalIncTax"),
      field("total_discount", "string (decimal)", "totalDiscount"),
      field("tip", "string (decimal)", "tip"),
    ],
  },
  {
    key: "lines",
    fields: [
      field("quantity", "integer", "quantity"),
      field("unit_price_inc_tax", "string (decimal)", "unitPriceIncTax"),
      field("unit_price_ex_tax", "string (decimal)", "unitPriceExTax"),
      field("line_total_inc_tax", "string (decimal)", "lineTotalIncTax"),
      field("line_total_ex_tax", "string (decimal)", "lineTotalExTax"),
      field("line_tax", "string (decimal)", "lineTax"),
      field("line_discount_total", "string (decimal)", "lineDiscountTotal"),
      field("applied_discounts", "object[]", "appliedDiscounts"),
      field("percent", "string (decimal)", "percent"),
    ],
  },
  {
    key: "payments",
    fields: [
      field("payment_type", "string", "paymentType"),
      field("amount_ex_tip", "string (decimal)", "amountExTip"),
      // YOSHI-10464: `amount` is the one name the API reuses - it is a charge inside a payment and
      // a discounted amount inside applied_discounts - so its description states both readings.
      // A note painted over an example could not: it would have hung on whichever came first.
      field("amount", "string (decimal)", "amount"),
      field("paid_at", "string (date-time)", "paidAt"),
      field("payments_count", "integer", "paymentsCount"),
      field("total_ex_tip", "string (decimal)", "totalExTip"),
      field("total_tip", "string (decimal)", "totalTip"),
      field("total_amount", "string (decimal)", "totalAmount"),
    ],
  },
  {
    key: "token",
    fields: [
      field("token_type", "string", "tokenType"),
      field("expires_in", "integer", "expiresIn"),
      field("expires_at", "string (date-time)", "expiresAt"),
      field("scopes", "string[]", "scopes"),
      field("error_code", "string", "errorCode"),
      field("message", "string", "message"),
      field("type", "string", "type"),
    ],
  },
];
